'use client'
import { ProjectListSkeleton } from '@/components/project/project-skeleton'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useCurrentUser } from '@/hooks/use-current-user'
import { useWorkspaces } from '@/hooks/subscriptions/use-workspaces'
import type { UserProject } from '@/lib/types'
import { cn, fetcher, isUserPremium } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import useSWRInfinite from 'swr/infinite'
import { ProjectCard } from '@/components/home-v3/projects/user-and-community-projects'

interface IProjectList {
  currentProjectId?: string
  className?: string
  maxColumns?: number
  showSkeletonHeader?: boolean
  linkToNewTab?: boolean
  cardClassName?: string
  /**
   * Filter projects:
   * - 'owned': Only show projects from workspaces the user owns
   * - 'shared': Only show projects from workspaces the user joined (but doesn't own)
   * - undefined: Show all projects
   */
  filter?: 'owned' | 'shared'
}

export const ProjectList = ({
  currentProjectId,
  className,
  maxColumns = 3,
  showSkeletonHeader = true,
  linkToNewTab = true,
  cardClassName,
  filter,
}: IProjectList) => {
  const t = useTranslations('project-preview')
  const { data: user } = useCurrentUser()
  const { workspaces } = useWorkspaces()

  // Get owned workspace IDs
  const ownedWorkspaceIds = workspaces
    .filter((w) => w.is_owner)
    .map((w) => w._id.toString())

  const limit = 20
  const { data, error, size, setSize, isLoading, isValidating, mutate } =
    useSWRInfinite((pageIndex: number) => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (pageIndex * limit).toString(),
      })
      return `/api/me/projects?${params}`
    }, fetcher)

  const allProjects: UserProject[] = data ? data.flat() : []

  // Filter projects based on ownership
  const projects = allProjects.filter((project) => {
    if (!filter) {
      return true
    }

    const isOwnedProject =
      project.user_id === user?._id?.toString() ||
      (project.workspace_id && ownedWorkspaceIds.includes(project.workspace_id))

    if (filter === 'owned') {
      return isOwnedProject
    }
    if (filter === 'shared') {
      return !isOwnedProject
    }
    return true
  })

  if (error) return <p className="text-red-500">{t('failed')}</p>
  // Show loading skeleton initially, only return null after loading completes with no projects
  if (!isLoading && (!projects || projects.length === 0)) return null
  const showHosting = (user && !isUserPremium(user)) || false

  /**
   * Check if a project is shared (not owned by current user)
   * A project is considered shared if:
   * - The user is not the direct owner (user_id doesn't match)
   * - AND the project belongs to a workspace the user doesn't own
   */
  const isProjectShared = (project: UserProject): boolean => {
    const isDirectOwner = project.user_id === user?._id?.toString()
    const isWorkspaceOwner =
      project.workspace_id && ownedWorkspaceIds.includes(project.workspace_id)
    return !isDirectOwner && !isWorkspaceOwner
  }

  return (
    <>
      <ScrollArea className="h-[40vh]">
        {isLoading && (
          <ProjectListSkeleton
            maxColumns={maxColumns}
            showSkeletonHeader={showSkeletonHeader}
          />
        )}
        <div
          x-test-id="project-list"
          className={cn(
            'grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-4',
            className,
          )}
        >
          {projects
            .filter((project) => project._id.toString() !== currentProjectId)
            .map((project) => {
              const isShared = isProjectShared(project)
              const isOwner = !isShared
              // If project has workspace_id and user can see it, user is a workspace member
              const isWorkspaceMember = Boolean(project.workspace_id)
              return (
                <ProjectCard
                  key={project._id.toString()}
                  project={project}
                  linkToNewTab={linkToNewTab}
                  isOwner={isOwner}
                  isShared={isShared}
                  isWorkspaceMember={isWorkspaceMember}
                  showActions
                  canDelete={isOwner}
                  cardClassName={cn(
                    'border-none hover:shadow-md',
                    cardClassName,
                  )}
                  showAvatar={false}
                  mutate={mutate}
                  showHosting={showHosting}
                  compact
                />
              )
            })}
        </div>
        <Button
          className="w-full my-4"
          variant="outline_2"
          onClick={() => setSize(size + 1)}
          disabled={isValidating}
        >
          {isValidating ? t('loading') : t('load_more')}
        </Button>
      </ScrollArea>

      <div className="h-4" />
    </>
  )
}
